/**
 * Tenant secrets request/response shapes.
 *
 * Endpoints (per RLY-023 ACs):
 *   POST   /api/v1/secrets         → CreateSecretRequest → SecretCreateResponse (201)
 *   GET    /api/v1/secrets         → SecretListResponse  (NAMES ONLY, never plaintext)
 *   DELETE /api/v1/secrets/:name   → 204
 *
 * Sprint-05 Note: an earlier draft of IMPLEMENTATION.md §9.1 used
 * `PUT /api/v1/secrets/:name` with body `{ value }`; the RLY-023 dispatch
 * brief authoritatively pins the create surface as
 * `POST /api/v1/secrets` with body `{ name, value }`. The legacy
 * `SetSecretRequest` shape remains exported for tooling continuity but is
 * not the active create-route schema.
 *
 * Hard rule: read responses NEVER include the secret value. Plaintext is
 * resolved server-side at run dispatch through `SecretsService.fetch()`
 * (IMPLEMENTATION.md §6.2). This is enforced both by the schema (no `value`
 * field on read) and by the secrets service authorization invariant (§6.4).
 */
import { z } from "zod";
import { IsoTimestampSchema } from "./common.js";
/**
 * Secret name — letters, digits, dashes, underscores. We disallow leading or
 * trailing punctuation to avoid ambiguous keys. 1-64 chars to comfortably fit
 * inside HTTP header / URL segments without encoding.
 */
export const SecretNameSchema = z
    .string()
    .regex(/^[A-Za-z0-9](?:[A-Za-z0-9_-]{0,62}[A-Za-z0-9])?$/)
    .describe("Secret name — alphanumeric + `_-`, 1-64 chars");
/** PUT /api/v1/secrets/:name body. */
export const SetSecretRequestSchema = z.object({
    value: z
        .string()
        .min(1)
        .max(64 * 1024),
    metadata: z.record(z.string()).optional(),
});
/**
 * Single secret as returned by list / set endpoints. Note the absence of a
 * `value` field — this is intentional and load-bearing per the AC.
 */
export const SecretListItemResponseSchema = z.object({
    name: SecretNameSchema,
    created_at: IsoTimestampSchema,
    rotated_at: IsoTimestampSchema.nullable(),
    metadata: z.record(z.string()).default({}),
});
export const SecretListResponseSchema = z.object({
    items: z.array(SecretListItemResponseSchema),
});
/**
 * RLY-023 — `POST /api/v1/secrets` request body. Carries the name and value
 * together so the create surface is a single round-trip. The value is
 * write-only — it is NEVER echoed in the response or in any `GET` payload.
 */
export const CreateSecretRequestSchema = z.object({
    name: SecretNameSchema,
    value: z
        .string()
        .min(1)
        .max(64 * 1024)
        .describe("Plaintext secret value. Server-only; never echoed in any response."),
    metadata: z.record(z.string()).optional(),
});
/**
 * RLY-023 — `POST /api/v1/secrets` response body (201 Created). Returns
 * name + created_at metadata only; the value is intentionally absent.
 *
 * AC #1: "Returns `201 { name, created_at }`. Never echoes the value."
 */
export const SecretCreateResponseSchema = z.object({
    name: SecretNameSchema,
    created_at: IsoTimestampSchema,
});
//# sourceMappingURL=secrets.js.map